import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Info } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { validateLauncherCss } from "../../lib/launcherCssValidator";
import ColorField from "./ColorField";
import UrlField from "./UrlField";
import DirtyDot from "./DirtyDot";
import LauncherCustomizationInfoModal from "./LauncherCustomizationInfoModal";

const groupDiffers = (a, b, keys) =>
  keys.some((k) => (a?.[k] ?? null) !== (b?.[k] ?? null));

// Launcher geometry controls have a meaningful default: an unset value renders
// identically to its default, so an absent baseline and an explicit default must
// compare equal — otherwise toggling a control to (or back to) its default would
// light up a spurious dirty dot against a never-saved baseline. Single source of
// truth for both the control fallbacks below and the dirty comparison.
const LAUNCHER_DEFAULTS = { shape: "pill", size: 56, animation: "none" };
const POSITION_DEFAULTS = { corner: "bottom-right", offsetX: 20, offsetY: 20 };

const groupDiffersWithDefaults = (a, b, defaults) =>
  Object.keys(defaults).some(
    (k) => (a?.[k] ?? defaults[k]) !== (b?.[k] ?? defaults[k]),
  );


const ThemeSection = ({ theme, baseline, onChange }) => {
  const { translate } = useLanguage();
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // null | "launcher" | "panel" — drives the info modal surface
  const [openInfo, setOpenInfo] = useState(null);
  // Inline error for theme.launcherCustomCSS. Fast feedback only; backend
  // remains the authoritative validator (returns 422 on save). Re-runs whenever
  // the prop changes — covers typing AND external resets (Discard / Reset).
  const [launcherCssError, setLauncherCssError] = useState(null);
  useEffect(() => {
    const r = validateLauncherCss(theme?.launcherCustomCSS || "");
    setLauncherCssError(r.ok ? null : r.error);
  }, [theme?.launcherCustomCSS]);

  const set = (path, value) => {
    // path is a dot-string like "primary" or "launcher.bg"
    const next = structuredClone(theme || {});
    const parts = path.split(".");
    let obj = next;
    for (let i = 0; i < parts.length - 1; i++) {
      if (obj[parts[i]] == null) obj[parts[i]] = {};
      obj = obj[parts[i]];
    }
    if (value === null || value === "") delete obj[parts[parts.length - 1]];
    else obj[parts[parts.length - 1]] = value;
    onChange(next);
  };

  const t = theme || {};
  const launcher = t.launcher || {};
  const position = launcher.position || {};
  const base = baseline || {};
  const baseLauncher = base.launcher || {};
  const basePosition = baseLauncher.position || {};
  const coloursDirty = groupDiffers(t, base, ["primary", "secondary"]);
  const launcherFieldsDirty =
    groupDiffers(launcher, baseLauncher, ["bg", "text", "iconUrl"]) ||
    groupDiffersWithDefaults(launcher, baseLauncher, LAUNCHER_DEFAULTS);
  const positionDirty = groupDiffersWithDefaults(
    position,
    basePosition,
    POSITION_DEFAULTS,
  );
  const launcherDirty = launcherFieldsDirty || positionDirty;
  const logoDirty = (t.logoUrl ?? null) !== (base.logoUrl ?? null);
  const advancedDirty =
    (t.panelCustomCSS ?? null) !== (base.panelCustomCSS ?? null) ||
    (t.launcherCustomCSS ?? null) !== (base.launcherCustomCSS ?? null);
  const sectionDirty = coloursDirty || launcherDirty || logoDirty || advancedDirty;

  const panelCss = t.panelCustomCSS || "";
  const launcherCss = t.launcherCustomCSS || "";

  return (
    <div className="rounded-xl overflow-hidden"
      style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border)" }}>
      <div className="px-6 py-4" style={{ borderBottom: "1px solid var(--color-border)" }}>
        <h2 className="flex items-center gap-2 text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>
          {translate("theme") || "Theme"}
          {sectionDirty && <DirtyDot />}
        </h2>
      </div>

      <div className="px-6 py-5 space-y-5">
        {/* Colours */}
        <div>
          <h3 className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide mb-3"
            style={{ color: "var(--color-text-muted)" }}>
            {translate("colors") || "Colours"}
            {coloursDirty && <DirtyDot />}
          </h3>
          <div className="space-y-2">
            <ColorField
              label={translate("primaryColor") || "Primary"}
              value={t.primary}
              onChange={(v) => set("primary", v)}
              defaultValue="#1F1F1F"
            />
            <ColorField
              label={translate("secondaryColor") || "Secondary"}
              value={t.secondary}
              onChange={(v) => set("secondary", v)}
              defaultValue="#FFFFFF"
            />
          </div>
        </div>

        {/* Launcher */}
        <div>
          <h3 className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide mb-3"
            style={{ color: "var(--color-text-muted)" }}>
            {translate("launcher") || "Launcher"}
            {launcherDirty && <DirtyDot />}
          </h3>
          <div className="space-y-2">
            <ColorField
              label={translate("launcherBackground") || "Background"}
              value={launcher.bg}
              onChange={(v) => set("launcher.bg", v)}
              defaultValue="#1F1F1F"
            />
            <ColorField
              label={translate("launcherText") || "Text"}
              value={launcher.text}
              onChange={(v) => set("launcher.text", v)}
              defaultValue="#FFFFFF"
            />
            <UrlField
              label={translate("launcherIcon") || "Icon URL"}
              value={launcher.iconUrl}
              onChange={(v) => set("launcher.iconUrl", v)}
            />
          </div>

          {/* Launcher geometry */}
          <div className="mt-4 space-y-3">
            {/* Shape */}
            <div>
              <span className="block text-xs font-medium uppercase tracking-wide mb-2"
                style={{ color: "var(--color-text-primary)" }}>
                {translate("launcherShape") || "Shape"}
              </span>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm"
                  style={{ color: "var(--color-text-primary)" }}>
                  <input
                    type="radio"
                    name="launcher-shape"
                    value="pill"
                    checked={(launcher.shape ?? LAUNCHER_DEFAULTS.shape) === "pill"}
                    onChange={() => set("launcher.shape", "pill")}
                  />
                  {translate("launcherShapePill") || "Pill"}
                </label>
                <label className="flex items-center gap-2 text-sm"
                  style={{ color: "var(--color-text-primary)" }}>
                  <input
                    type="radio"
                    name="launcher-shape"
                    value="circle"
                    checked={(launcher.shape ?? LAUNCHER_DEFAULTS.shape) === "circle"}
                    onChange={() => set("launcher.shape", "circle")}
                  />
                  {translate("launcherShapeCircle") || "Circle"}
                </label>
              </div>
            </div>

            {/* Size */}
            <div>
              <label className="block text-xs font-medium uppercase tracking-wide mb-2"
                style={{ color: "var(--color-text-primary)" }}>
                {translate("launcherSize") || "Size"}
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={32}
                  max={96}
                  step={2}
                  value={launcher.size ?? LAUNCHER_DEFAULTS.size}
                  onChange={(e) => set("launcher.size", Number(e.target.value))}
                  className="flex-1"
                  aria-label={translate("launcherSize") || "Size"}
                />
                <span className="text-sm tabular-nums w-12 text-right"
                  style={{ color: "var(--color-text-secondary)" }}>
                  {launcher.size ?? LAUNCHER_DEFAULTS.size}
                </span>
              </div>
            </div>

            {/* Corner */}
            <div>
              <span className="block text-xs font-medium uppercase tracking-wide mb-2"
                style={{ color: "var(--color-text-primary)" }}>
                {translate("launcherCorner") || "Corner"}
              </span>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm"
                  style={{ color: "var(--color-text-primary)" }}>
                  <input
                    type="radio"
                    name="launcher-corner"
                    value="bottom-right"
                    checked={(position.corner ?? POSITION_DEFAULTS.corner) === "bottom-right"}
                    onChange={() => set("launcher.position.corner", "bottom-right")}
                  />
                  {translate("launcherCornerBottomRight") || "Bottom-right"}
                </label>
                <label className="flex items-center gap-2 text-sm"
                  style={{ color: "var(--color-text-primary)" }}>
                  <input
                    type="radio"
                    name="launcher-corner"
                    value="bottom-left"
                    checked={(position.corner ?? POSITION_DEFAULTS.corner) === "bottom-left"}
                    onChange={() => set("launcher.position.corner", "bottom-left")}
                  />
                  {translate("launcherCornerBottomLeft") || "Bottom-left"}
                </label>
              </div>
            </div>

            {/* Offsets */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium uppercase tracking-wide mb-2"
                  style={{ color: "var(--color-text-primary)" }}>
                  {translate("launcherOffsetX") || "Offset X"}
                </label>
                <input
                  type="number"
                  min={0}
                  max={200}
                  value={position.offsetX ?? POSITION_DEFAULTS.offsetX}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "") return set("launcher.position.offsetX", null);
                    const clamped = Math.min(200, Math.max(0, Number(raw)));
                    set("launcher.position.offsetX", clamped);
                  }}
                  className="field-input w-full"
                />
              </div>
              <div>
                <label className="block text-xs font-medium uppercase tracking-wide mb-2"
                  style={{ color: "var(--color-text-primary)" }}>
                  {translate("launcherOffsetY") || "Offset Y"}
                </label>
                <input
                  type="number"
                  min={0}
                  max={200}
                  value={position.offsetY ?? POSITION_DEFAULTS.offsetY}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "") return set("launcher.position.offsetY", null);
                    const clamped = Math.min(200, Math.max(0, Number(raw)));
                    set("launcher.position.offsetY", clamped);
                  }}
                  className="field-input w-full"
                />
              </div>
            </div>

            {/* Animation */}
            <div>
              <label className="block text-xs font-medium uppercase tracking-wide mb-2"
                style={{ color: "var(--color-text-primary)" }}>
                {translate("launcherAnimation") || "Animation"}
              </label>
              <select
                value={launcher.animation ?? LAUNCHER_DEFAULTS.animation}
                onChange={(e) => set("launcher.animation", e.target.value)}
                className="field-input w-full"
              >
                <option value="none">{translate("launcherAnimationNone") || "None"}</option>
                <option value="float">{translate("launcherAnimationFloat") || "Float"}</option>
                <option value="pulse">{translate("launcherAnimationPulse") || "Pulse"}</option>
                <option value="bounce">{translate("launcherAnimationBounce") || "Bounce"}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Logo */}
        <div>
          <h3 className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide mb-3"
            style={{ color: "var(--color-text-muted)" }}>
            {translate("logo") || "Logo"}
            {logoDirty && <DirtyDot />}
          </h3>
          <UrlField
            label={translate("logoUrl") || "Logo URL"}
            value={t.logoUrl}
            onChange={(v) => set("logoUrl", v)}
          />
        </div>

        {/* Custom CSS */}
        <div>
          <button
            type="button"
            onClick={() => setAdvancedOpen((o) => !o)}
            className="flex items-center gap-2 text-sm font-medium"
            style={{ color: "var(--color-text-secondary)" }}
          >
            {advancedOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            {translate("customCSS") || "Custom CSS"}
            {advancedDirty && <DirtyDot />}
          </button>
          {advancedOpen && (
            <div className="mt-3">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium uppercase tracking-wide"
                    style={{ color: "var(--color-text-primary)" }}>
                    {translate("customCSSPanel") || "Custom CSS — Panel"}
                  </label>
                  <button
                    type="button"
                    onClick={() => setOpenInfo("panel")}
                    aria-label={translate("infoHelpLabelPanel") || "Help: panel customization"}
                    className="p-1 rounded hover:opacity-80"
                    style={{ color: "var(--color-text-muted)" }}
                  >
                    <Info size={14} />
                  </button>
                </div>
                <textarea
                  value={panelCss}
                  onChange={(e) => set("panelCustomCSS", e.target.value)}
                  rows={12}
                  className="field-input resize-y w-full"
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: "12px",
                    lineHeight: "1.5",
                  }}
                  placeholder={translate("customCSSPanelPlaceholder") ||
                    ".kodly-ai-agent-panel {\n  padding: 12px;\n}"}
                  maxLength={50 * 1024}
                />
                <p className="text-xs mt-1 text-right" style={{ color: "var(--color-text-muted)" }}>
                  {panelCss.length} / {50 * 1024} bytes
                </p>
              </div>
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium uppercase tracking-wide"
                    style={{ color: "var(--color-text-primary)" }}>
                    {translate("customCSSLauncher") || "Custom CSS — Launcher"}
                  </label>
                  <button
                    type="button"
                    onClick={() => setOpenInfo("launcher")}
                    aria-label={translate("infoHelpLabelLauncher") || "Help: launcher customization"}
                    className="p-1 rounded hover:opacity-80"
                    style={{ color: "var(--color-text-muted)" }}
                  >
                    <Info size={14} />
                  </button>
                </div>
                <textarea
                  value={launcherCss}
                  onChange={(e) => set("launcherCustomCSS", e.target.value)}
                  rows={12}
                  className="field-input resize-y w-full"
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: "12px",
                    lineHeight: "1.5",
                  }}
                  placeholder={translate("customCSSLauncherPlaceholder") ||
                    ".kodly-ai-agent-launcher:hover {\n  transform: scale(1.1);\n}"}
                  maxLength={50 * 1024}
                  aria-invalid={launcherCssError ? "true" : undefined}
                />
                {launcherCssError && (
                  <p role="alert" className="text-xs mt-1" style={{ color: "var(--color-error)" }}>
                    {launcherCssError}
                  </p>
                )}
                <p className="text-xs mt-1 text-right" style={{ color: "var(--color-text-muted)" }}>
                  {launcherCss.length} / {50 * 1024} bytes
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <LauncherCustomizationInfoModal
        isOpen={openInfo !== null}
        surface={openInfo}
        onClose={() => setOpenInfo(null)}
      />
    </div>
  );
};

export default ThemeSection;
